import { getPendingIncidents, getSyncStats, initDB, getOwnedOperations } from './offlineStorage';
import { deliverIncident } from './submissionQueue';
import { readSession, sessionIsCurrent, SESSION_CHANGED, STORAGE_CHANGED } from './submissionSession';

const listeners = new Set(), active = new Map();
export const onSyncStatusChange = callback => { listeners.add(callback); return () => listeners.delete(callback); };
const notify = value => listeners.forEach(callback => { try { callback(value); } catch { /* A view cannot interrupt persistence. */ } });
export const isOnline = () => navigator.onLine;
export async function syncPendingIncidents(token) {
  const session = readSession(token);
  if (!session || !isOnline()) return { synced: 0, failed: 0 };
  const rows = await getPendingIncidents(session.ownerUserId);
  let synced = 0, failed = 0;
  for (const row of rows) {
    if (!sessionIsCurrent(session)) break;
    notify({ type: 'syncing', ownerUserId: session.ownerUserId, message: 'Sending saved calls?' });
    const result = await deliverIncident(row.localId, token);
    if (result.accepted) synced++;
    else { failed++; if (result.record?.syncStatus === 'pending') break; }
  }
  return { synced, failed };
}
export async function processSyncQueue(token) {
  const session = readSession(token);
  if (!session) return { processed: 0, failed: 0 };
  // Older mutations have no server idempotency contract. Preserve them for explicit review.
  const rows = await getOwnedOperations(session.ownerUserId);
  return { processed: 0, failed: rows.filter(row => row.status !== 'completed').length };
}
export function runFullSync(token) {
  const session = readSession(token);
  if (!session) return Promise.resolve(null);
  if (active.has(session.ownerUserId)) return active.get(session.ownerUserId);
  const task = (async () => {
    notify({ type: 'sync-start', ownerUserId: session.ownerUserId, message: 'Checking saved work?' });
    try {
      const incidents = await syncPendingIncidents(token), queue = await processSyncQueue(token), stats = await getSyncStats();
      const unresolved = incidents.failed + queue.failed + stats.pendingIncidents + stats.syncingIncidents + stats.errorIncidents;
      const result = { incidents, queue, stats };
      notify({ type: 'sync-complete', ownerUserId: session.ownerUserId, message: unresolved ? 'Saved work remains on this device. Review Pending calls.' : 'All saved calls have server confirmation.', results: result });
      return result;
    } catch (error) {
      notify({ type: 'sync-error', ownerUserId: session.ownerUserId, message: 'Saved work is retained. ' + error.message });
      throw error;
    }
  })().finally(() => active.delete(session.ownerUserId));
  active.set(session.ownerUserId, task); return task;
}
export function initSyncManager(getToken) {
  const attempt = () => { const token = getToken(); if (token && isOnline()) runFullSync(token).catch(() => {}); };
  initDB().then(attempt).catch(() => notify({ type: 'sync-error', message: 'Local storage is unavailable. Calls cannot be sent until a local copy is saved.' }));
  const offline = () => notify({ type: 'offline', message: 'Offline. Saved calls are retained on this device.' });
  const worker = event => { if (event.data?.type === 'SYNC_QUEUE') attempt(); };
  window.addEventListener('online', attempt); window.addEventListener('offline', offline);
  window.addEventListener(SESSION_CHANGED, attempt);
  navigator.serviceWorker?.addEventListener('message', worker);
  const timer = setInterval(attempt, 15000);
  return () => {
    clearInterval(timer); window.removeEventListener('online', attempt); window.removeEventListener('offline', offline);
    window.removeEventListener(SESSION_CHANGED, attempt); navigator.serviceWorker?.removeEventListener('message', worker);
  };
}
export async function requestBackgroundSync() {
  const registration = window.swRegistration;
  if (registration?.sync) await registration.sync.register('sync-incidents');
  window.dispatchEvent(new Event(STORAGE_CHANGED));
}
